import { useMemo, useState } from "react";
import useUpfrontPayments from "../../../components/hooks/useUpfrontPayments";
import { downloadCsv } from "../../../utils/util";
import PaymentsRangeFilter, {
  PaymentsRange,
  rangeLabel,
} from "./PaymentsRangeFilter";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSButton from "views/system-design/DSButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import {
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
} from "views/system-design/Table";

const CURRENCY = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

/** 'YYYY-MM' -> 'Jan 2026'. */
const formatMonth = (entitlementMonth: string) => {
  const [year, month] = entitlementMonth.split("-").map(Number);
  if (!year || !month) return entitlementMonth;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

function UpfrontPaymentsWidget() {
  const [range, setRange] = useState<PaymentsRange>({});

  const { data, isLoading, isError } = useUpfrontPayments(range);
  const payments = data ?? [];

  const total = useMemo(
    () => payments.reduce((sum, payment) => sum + payment.upfrontAmount, 0),
    [payments],
  );

  const exportCsv = () => {
    downloadCsv({
      filename: `upfront-payments_${rangeLabel(range)}.csv`,
      headers: [
        "Artist",
        "Collection",
        "Entitlement Month",
        "Upfront Amount (USD)",
      ],
      rows: payments.map((payment) => [
        payment.artistName || payment.artistId,
        payment.collectionName || "Untitled collection",
        payment.entitlementMonth,
        payment.upfrontAmount.toFixed(2),
      ]),
    });
  };

  const numericCellSx = { textAlign: "right" as const, whiteSpace: "nowrap" };

  return (
    <DSCard
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "20px",
      }}
    >
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <DSTypography variant="h6">Artist Upfront Payments</DSTypography>
        <DSStack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          <PaymentsRangeFilter onChange={setRange} />
          <DSButton
            size="small"
            variant="outlined"
            disabled={!payments.length}
            onClick={exportCsv}
          >
            Export CSV
          </DSButton>
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1, overflowX: "auto" }}>
        {isLoading ? (
          <DSBox sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <DSCircularProgress />
          </DSBox>
        ) : isError && !data ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            Failed to load upfront payments
          </DSTypography>
        ) : !payments.length ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            No upfront payments in this period
          </DSTypography>
        ) : (
          <DSTable size="small">
            <DSTableHead>
              <DSTableRow>
                <DSTableCell>Artist</DSTableCell>
                <DSTableCell>Collection</DSTableCell>
                <DSTableCell>Entitlement Month</DSTableCell>
                <DSTableCell sx={numericCellSx}>Upfront Amount</DSTableCell>
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {payments.map((payment) => (
                <DSTableRow key={payment.collectionId} hover>
                  <DSTableCell>
                    {payment.artistName || payment.artistId}
                  </DSTableCell>
                  <DSTableCell>
                    {payment.collectionName || "Untitled collection"}
                  </DSTableCell>
                  <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatMonth(payment.entitlementMonth)}
                  </DSTableCell>
                  <DSTableCell sx={{ ...numericCellSx, fontWeight: 600 }}>
                    {CURRENCY.format(payment.upfrontAmount)}
                  </DSTableCell>
                </DSTableRow>
              ))}
              <DSTableRow>
                <DSTableCell colSpan={3} sx={{ fontWeight: 700 }}>
                  Total ({payments.length}{" "}
                  {payments.length === 1 ? "payment" : "payments"})
                </DSTableCell>
                <DSTableCell sx={{ ...numericCellSx, fontWeight: 700 }}>
                  {CURRENCY.format(total)}
                </DSTableCell>
              </DSTableRow>
            </DSTableBody>
          </DSTable>
        )}
      </DSCardContent>
      <DSBox sx={{ px: 2, py: 1.5 }}>
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          One row per advanced collection. The upfront is posted to CollabPay
          when the collection is approved, so the entitlement month is the
          approval month — recouped advances are included.
        </DSTypography>
      </DSBox>
    </DSCard>
  );
}

export default UpfrontPaymentsWidget;
